import { useState, useEffect, useCallback } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Button, Alert } from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import ViewListIcon from '@mui/icons-material/ViewList';
import AddIcon from '@mui/icons-material/Add';
import { getClients } from '../../../services/clientService';
import { getSuccessRequests } from '../../../services/successRequestService';
import RequestsDashboard from './RequestsDashboard';
import RequestsList from './RequestsList';
import RequestFormDialog from './RequestFormDialog';
import { sortByText } from '../../../utils/sortLists';

const BLUE = '#004df2';

// Parent for the Implementations "Requests" tab. Owns the shared data fetch
// (all active requests + the client list), the view switch, and the single
// add/view/edit dialog — so the "New Request" action lives in the header
// alongside the Dashboard / All Requests toggle.
const Requests = ({ openRequestId = null }) => {
    const [view, setView] = useState('dashboard');
    const [requests, setRequests] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setRequests(await getSuccessRequests());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        getClients()
            .then((d) => setClients(sortByText(d.clients || [], (c) => c.name)))
            .catch(() => setClients([]));
    }, []);

    // Deep link from the work views: /implementations?request=<id>. Waits for
    // the list, then opens that request and switches to the list behind it, so
    // closing the dialog leaves the reader somewhere that makes sense rather
    // than on a dashboard that never mentioned it.
    const [deepLinked, setDeepLinked] = useState(false);
    useEffect(() => {
        if (deepLinked || !openRequestId || !requests.length) return;
        const found = requests.find(
            (r) => String(r.NexusSuccessRequest_Id) === String(openRequestId),
        );
        if (!found) return;
        setEditing(found);
        setDialogOpen(true);
        setView('list');
        setDeepLinked(true);
    }, [openRequestId, requests, deepLinked]);

    const openCreate = () => { setEditing(null); setDialogOpen(true); };
    const openRequest = (row) => { setEditing(row); setDialogOpen(true); };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                    value={view}
                    exclusive
                    size="small"
                    onChange={(_, v) => v && setView(v)}
                    sx={{ '& .MuiToggleButton-root.Mui-selected': { backgroundColor: 'rgba(0,77,242,0.12)', color: BLUE } }}
                >
                    <ToggleButton value="dashboard" sx={{ textTransform: 'none' }}>
                        <InsightsIcon sx={{ fontSize: 16, mr: 0.75 }} /> Dashboard
                    </ToggleButton>
                    <ToggleButton value="list" sx={{ textTransform: 'none' }}>
                        <ViewListIcon sx={{ fontSize: 16, mr: 0.75 }} /> All Requests
                    </ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ flexGrow: 1 }} />

                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}
                    sx={{ textTransform: 'none', backgroundColor: BLUE, '&:hover': { backgroundColor: '#0040c8' } }}>
                    New Request
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {view === 'dashboard'
                ? <RequestsDashboard requests={requests} loading={loading} />
                : <RequestsList requests={requests} loading={loading} onRefresh={refresh} onOpenRequest={openRequest} />}

            <RequestFormDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                request={editing}
                clients={clients}
                onSaved={refresh}
            />
        </Box>
    );
};

export default Requests;
